// Landing page content service.
// Endpoints:
//  GET    /api/landing                (public, no auth) -> content | null
//  GET    /api/admin/landing          -> content | null (null = never customised)
//  PUT    /api/admin/landing          body { content }
//  DELETE /api/admin/landing          reset to the built-in defaults
//  POST   /api/admin/landing/upload   multipart field "image" -> { url }

import { API_BASE_URL } from './apiConfig';

const ADMIN_LANDING_BASE = `${API_BASE_URL}/admin/landing`;
const PUBLIC_LANDING_BASE = `${API_BASE_URL}/landing`;

export type SectionType =
  | 'hero'
  | 'about'
  | 'features'
  | 'videos'
  | 'how_it_works'
  | 'pricing'
  | 'custom';

export interface SectionConfig {
  id: string;
  type: SectionType;
  enabled: boolean;
}

export interface LinkTarget {
  text: string;
  link: string;
}

export interface IconText {
  icon: string;
  text: string;
}

export interface LandingContent {
  nav: {
    logo: string;
    logoAlt: string;
    showTrialButton: boolean;
    trialText: string;
    trialLink: string;
    loginText: string;
    loginLink: string;
  };
  // Thin notice bar fixed to the top of the landing page (e.g. maintenance)
  announcement: {
    enabled: boolean;
    text: string;
  };
  sections: SectionConfig[];
  hero: {
    slideIntervalSec: number;
    slides: {
      image: string;
      alt: string;
      titleLead: string[];
      titleHighlight: string;
      subtitle: string;
    }[];
    primaryButton: LinkTarget;
    secondaryButton: LinkTarget & { enabled: boolean };
    stats: { icon: string; number: string; label: string }[];
    categories: { title: string; image: string; link: string }[];
  };
  about: {
    title: string;
    paragraphs: string[];
    image: string;
    imageAlt: string;
    chips: IconText[];
  };
  features: {
    title: string;
    subtitle: string;
    items: { icon: string; title: string; description: string }[];
  };
  videos: {
    title: string;
    subtitle: string;
    items: { title: string; url: string }[];
  };
  howItWorks: {
    title: string;
    subtitle: string;
    steps: { title: string; description: string }[];
  };
  pricing: {
    title: string;
    subtitle: string;
    footnote: string;
    ctaLink: string;
    extraPlan: {
      enabled: boolean;
      name: string;
      tagline: string;
      price: string;
      period: string;
      badge: string;
      cta_text: string;
      // Independent from the shared ctaLink above -- this is the one button
      // that's meant to go somewhere different (the Contact Sales page)
      // rather than straight to /register like every other plan's button.
      cta_link: string;
      features: string[];
    };
  };
  // A standalone page (/contact-sales), not an in-page section -- reached
  // from the Enterprise/Channel Partner plan's "Contact Sales" button via
  // pricing.extraPlan.cta_link.
  contactSales: {
    subheading: string;
    description: string;
    highlights: string[];
    contactName: string;
    contactRole: string;
    companyName: string;
    phone: string;
    email: string;
  };
  customSections: Record<
    string,
    {
      title: string;
      body: string;
      image: string;
      imagePosition: 'left' | 'right';
      buttonText: string;
      buttonLink: string;
    }
  >;
  footer: {
    logo: string;
    cta: { enabled: boolean; title: string; text: string; buttonText: string; buttonLink: string };
    tagline: string;
    email: string;
    phone: string;
    address: string;
    columns: {
      title: string;
      links: { label: string; kind: FooterLinkKind; target: string }[];
    }[];
    copyright: string;
    showStats: boolean;
  };
}

// section  -> scroll to a section on the page (target = section anchor id)
// route    -> in-app route, e.g. /login
// url      -> external link
// terms    -> opens the Terms & Conditions modal
// text     -> plain, non-clickable text
export type FooterLinkKind = 'section' | 'route' | 'url' | 'terms' | 'text';

function getAuthHeaders(json = true): HeadersInit {
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';
  let adminToken: string | null = null;
  try {
    adminToken = sessionStorage.getItem('adminToken');
    if (!adminToken) {
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i) || '';
        if (k.startsWith('admin_token')) {
          adminToken = sessionStorage.getItem(k);
          break;
        }
      }
    }
  } catch {
    // ignore
  }
  const authToken = adminToken || localStorage.getItem('authToken');
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  return headers;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => null);
  if (!res.ok || !body || body.success === false) {
    throw new Error(body?.message || `Request failed (${res.status})`);
  }
  return body.data as T;
}

class LandingService {
  // Public, unauthenticated -- used by the landing page
  async getPublicContent(): Promise<Partial<LandingContent> | null> {
    const res = await fetch(PUBLIC_LANDING_BASE);
    return parseResponse<Partial<LandingContent> | null>(res);
  }

  async getAdminContent(): Promise<Partial<LandingContent> | null> {
    const res = await fetch(ADMIN_LANDING_BASE, { headers: getAuthHeaders() });
    return parseResponse<Partial<LandingContent> | null>(res);
  }

  async save(content: LandingContent): Promise<LandingContent> {
    const res = await fetch(ADMIN_LANDING_BASE, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content }),
    });
    return parseResponse<LandingContent>(res);
  }

  async reset(): Promise<void> {
    const res = await fetch(ADMIN_LANDING_BASE, { method: 'DELETE', headers: getAuthHeaders() });
    await parseResponse<null>(res);
  }

  // Resolves to a server path such as /uploads/landing/landing-123.jpg
  async uploadImage(file: File): Promise<string> {
    const form = new FormData();
    form.append('image', file);
    const res = await fetch(`${ADMIN_LANDING_BASE}/upload`, {
      method: 'POST',
      headers: getAuthHeaders(false),
      body: form,
    });
    const data = await parseResponse<{ url: string }>(res);
    return data.url;
  }
}

export default new LandingService();
